import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from "vue";
import { format } from "date-fns";
import { useI18n } from "vue-i18n-bridge";
import { NolaCoinClient } from "@/lib/clients/nolaCoinClient";
import { FishHistoryItem, FishTransactionType, EntryBreakdown, CoinType } from "@/lib/models/nolaCoin";
import ChevronLeftIcon from "icons/ChevronLeft.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";

// i18nの初期化

export default /*#__PURE__*/_defineComponent({
  __name: 'FishHistory',
  setup(__props) {

const { t } = useI18n();

// APIクライアントのインスタンス
const nolaCoinClient = new NolaCoinClient();

// 履歴データの状態管理
const historyItems = ref<FishHistoryItem[]>([]);
const isLoading = ref(false);
const hasMoreData = ref(true);
const error = ref<string | null>(null);
const batchError = ref<string | null>(null); // バッチ読み込みエラー用

// ページネーション設定
const currentPage = ref(1);
const itemsPerPage = ref(10); // 10件ずつ表示
const defaultLimit = ref(100); // デフォルトlimit

// API関連の状態管理
const currentBatchNumber = ref(1); // 現在のバッチ番号（1から開始）
const maxBatches = ref(5); // 最大バッチ数（合計500件まで）
const isLoadingBatch = ref(false); // バッチ読み込み中フラグ
const nextLastKey = ref<string | undefined>(undefined); // 次のページ用のlastKey

const hasMorePages = computed(() => currentBatchNumber.value < maxBatches.value && hasMoreData.value); // さらにページがあるかどうか

// 計算プロパティ
const totalItems = computed(() => historyItems.value.length);
const currentBatchPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() => Math.min(startIndex.value + itemsPerPage.value, totalItems.value));

const paginatedItems = computed(() => historyItems.value.slice(startIndex.value, endIndex.value));

// ページネーション対象のアイテムで有効期限付きのもの
const paginatedItemsWithExpiration = computed(() =>
  paginatedItems.value.map((item) => {
    const expirationDate = getExpirationDate(item);
    return {
      ...item,
      transactionTypeLabel: getTransactionTypeLabel(item.transactionType),
      expirationDateFormatted: expirationDate
        ? format(new Date(expirationDate), t("fishHistory.dateFormat.expiredAt"))
        : null,
      isNegative: item.amount < 0,
    };
  })
);

// 新しいページネーション表示ロジック
const visiblePages = computed(() => {
  const pages: Array<number | string> = [];
  const totalPages = currentBatchPages.value;
  const current = currentPage.value;

  // 基本的には：1 ... currentPage-1 currentPage currentPage+1 ... totalPages
  // ただし、隣接する場合は...を省略

  // データがない場合は空配列を返す
  if (totalPages === 0) {
    return pages;
  }

  // ページが1つだけの場合
  if (totalPages === 1) {
    pages.push(1);
    return pages;
  }

  // 最初のページ（1）を追加
  pages.push(1);

  // 現在のページが1の場合
  if (current === 1) {
    if (totalPages > 1) {
      pages.push(2);
    }
    // 2ページ以上のギャップがある場合のみ省略記号を表示
    if (totalPages > 4) {
      pages.push("...");
    }
    // 3ページ目がある場合は3を表示、ない場合は最後のページを表示
    if (totalPages === 3) {
      pages.push(3);
    } else if (totalPages === 4) {
      pages.push(3);
      pages.push(4);
    } else if (totalPages > 4) {
      pages.push(totalPages);
    }
    return pages;
  }

  // 現在のページが最後のページの場合
  if (current === totalPages) {
    // 2ページ以上のギャップがある場合のみ省略記号を表示
    if (totalPages > 4) {
      pages.push("...");
    }
    // 前のページを適切に表示
    if (totalPages === 2) {
      pages.push(2);
    } else if (totalPages === 3) {
      pages.push(2);
      pages.push(3);
    } else if (totalPages === 4) {
      pages.push(2);
      pages.push(3);
      pages.push(4);
    } else if (totalPages > 4) {
      pages.push(totalPages - 1);
      pages.push(totalPages);
    }
    return pages;
  }

  // 現在のページが中間の場合
  const prevPage = current - 1;
  const nextPage = current + 1;

  // 1とprevPageの間に...が必要かチェック
  if (prevPage > 2) {
    pages.push("...");
  }

  // 前のページを追加（1でない場合）
  if (prevPage > 1) {
    pages.push(prevPage);
  }

  // 現在のページを追加（1でない場合）
  if (current > 1) {
    pages.push(current);
  }

  // 次のページを追加（最後でない場合）
  if (nextPage < totalPages) {
    pages.push(nextPage);
  }

  // nextPageと最後のページの間に...が必要かチェック
  if (nextPage < totalPages - 1) {
    pages.push("...");
  }

  // 最後のページを追加（現在のページと異なる場合）
  if (totalPages > 1 && current !== totalPages) {
    pages.push(totalPages);
  }

  return pages;
});

// メソッド
const goToPage = (page: number) => {
  if (page >= 1 && page <= currentBatchPages.value) {
    currentPage.value = page;
    // ページ変更時にスクロール位置をトップに戻す
    window.scrollTo({ top: 0, behavior: "smooth" });
    // watcherが自動的に次のバッチを読み込むかどうかを判断
  }
};

// 最初のデータを読み込む
const loadInitialData = async () => {
  if (isLoading.value) return;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await nolaCoinClient.listTransactionHistory({
      limit: defaultLimit.value,
    });

    historyItems.value = response.items;
    hasMoreData.value = !!response.lastKey; // lastKeyが存在すれば次のページがある
    nextLastKey.value = response.lastKey;
  } catch (err) {
    console.error("Failed to load initial data:", err);
    error.value = t("fishHistory.errorMessage");
    historyItems.value = []; // エラー時は空配列にして、エラー表示を優先
    hasMoreData.value = false;
  } finally {
    isLoading.value = false;
  }
};

// 再試行関数
const retryLoadData = async () => {
  await loadInitialData();
};

// 新しいバッチを読み込む（実際のAPI版）
const loadNextBatch = async () => {
  if (isLoadingBatch.value || !hasMorePages.value) {
    return;
  }

  isLoadingBatch.value = true;
  batchError.value = null; // エラー状態をクリア

  try {
    const response = await nolaCoinClient.listTransactionHistory({
      limit: defaultLimit.value,
      lastKey: nextLastKey.value,
    });

    // 既存のデータに追加
    historyItems.value.push(...response.items);

    // バッチ番号を更新
    currentBatchNumber.value += 1;

    // 次のページ情報を更新
    hasMoreData.value = !!response.lastKey;
    nextLastKey.value = response.lastKey;
  } catch (err) {
    console.error("Failed to load batch data:", err);
    batchError.value = t("fishHistory.batchErrorMessage");
    // エラー時は追加読み込みを停止
    hasMoreData.value = false;
  } finally {
    isLoadingBatch.value = false;
  }
};

// バッチ読み込み再試行関数
const retryLoadBatch = async () => {
  // エラー状態をリセットして再試行
  hasMoreData.value = true;
  await loadNextBatch();
};

// TransactionTypeを翻訳するヘルパー関数
const getTransactionTypeLabel = (transactionType: FishTransactionType): string => {
  const key = `fishHistory.transactionType.${transactionType}`;
  const translated = t(key);
  // 翻訳が見つからない場合はトランザクションタイプをそのまま返す
  return translated === key ? transactionType : translated;
};

// 有効期限を取得するヘルパー関数
const getExpirationDate = (item: FishHistoryItem): string | null => {
  // entryBreakdownがあれば取引タイプに関係なくチェック
  if (item.entryBreakdown && item.entryBreakdown.length > 0) {
    // entryBreakdownから有効期限を取得
    const entryWithExpiration = item.entryBreakdown.find((entry) => entry.expiredAt);
    if (entryWithExpiration?.expiredAt) {
      return entryWithExpiration.expiredAt;
    }
  }

  return null;
};

// 有効期限が表示すべきものかを判定するヘルパー関数
const shouldShowExpiration = (expiredAt: string | undefined): boolean => {
  if (!expiredAt) return false;

  // 9999999999999（実質無期限）の場合は非表示
  const INFINITE_EXPIRATION = 9999999999999;
  const expiredAtTimestamp = new Date(expiredAt).getTime();

  return expiredAtTimestamp !== INFINITE_EXPIRATION;
};

// 有効期限をフォーマットするヘルパー関数
const formatExpiredAt = (expiredAt: string | undefined): string => {
  if (!expiredAt) return "";
  const dateFormat = t("fishHistory.dateFormat.expiredAt");
  return format(new Date(expiredAt), dateFormat);
};

// 作成日をフォーマットするヘルパー関数
const formatCreatedAt = (createdAt: string): string => {
  const dateFormat = t("fishHistory.dateFormat.createdAt");
  return format(new Date(createdAt), dateFormat);
};

const formatNumber = (value: number) => {
  const formattedValue = Math.abs(value).toLocaleString("ja-JP");
  return value >= 0 ? `+${formattedValue}` : `-${formattedValue}`;
};

// 内訳表示の判定
const shouldShowBreakdown = (item: FishHistoryItem): boolean =>
  !!(item.entryBreakdown && item.entryBreakdown.length > 0);

// 内訳エントリーを整形して取得
const getBreakdownEntries = (item: FishHistoryItem) => {
  if (!item.entryBreakdown) return [];

  return item.entryBreakdown.map((entry) => ({
    label: getBreakdownSourceLabel(entry),
    amount: Math.abs(entry.amount),
    expiredAt: entry.expiredAt,
  }));
};

// 内訳のソースラベルを取得するヘルパー関数
const getBreakdownSourceLabel = (entry: EntryBreakdown): string => {
  // coinTypeから判断（優先）
  if (entry.coinType) {
    switch (entry.coinType) {
      case CoinType.PREPAID_WEB:
        return t("fishHistory.coinType.chargeWeb");
      case CoinType.PREPAID_IOS:
        return t("fishHistory.coinType.chargeIOS");
      case CoinType.PREPAID_ANDROID:
        return t("fishHistory.coinType.chargeAndroid");
      case CoinType.BONUS:
      case CoinType.BASE:
        return t("fishHistory.coinType.bonus");
      default:
        return entry.coinType;
    }
  }
  return "";
};

// 現在のページが最後のページ近くになった時に自動的に次のバッチを読み込む
watch(
  [currentPage, currentBatchPages, hasMorePages],
  async ([newPage, batchPages, morePages]) => {
    // 最後のページにアクセスした時に次のバッチを読み込む
    if (newPage >= batchPages && morePages && !isLoadingBatch.value) {
      await loadNextBatch();
    }
  },
  { immediate: false }
);

// コンポーネントマウント時に初期データを読み込む
onMounted(() => {
  loadInitialData();
});

return { __sfc: true,t, nolaCoinClient, historyItems, isLoading, hasMoreData, error, batchError, currentPage, itemsPerPage, defaultLimit, currentBatchNumber, maxBatches, isLoadingBatch, nextLastKey, hasMorePages, totalItems, currentBatchPages, startIndex, endIndex, paginatedItems, paginatedItemsWithExpiration, visiblePages, goToPage, loadInitialData, retryLoadData, loadNextBatch, retryLoadBatch, getTransactionTypeLabel, getExpirationDate, shouldShowExpiration, formatExpiredAt, formatCreatedAt, formatNumber, shouldShowBreakdown, getBreakdownEntries, getBreakdownSourceLabel, ChevronLeftIcon, LoadingSpinner }
}

})